import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ArrowLeft, Clock, CheckCircle, Phone, Mail } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { publicAPI } from "../api/publicAPI";
import { getServicesData } from "../mock"; // Fallback
import Header from "./Header";
import Footer from "./Footer";

const ServicePage = () => {
  const { slug } = useParams();
  const { t, currentLanguage } = useLanguage();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadService();
  }, [slug, currentLanguage]);

  const loadService = async () => {
    try {
      setLoading(true);
      const data = await publicAPI.getServiceBySlug(slug, currentLanguage);
      setService(data);
      setError(null);
    } catch (err) {
      console.error("Failed to load service from API, using fallback:", err);
      // Fallback to mock data
      const servicesData = getServicesData(currentLanguage);
      const mockService = servicesData.find(s => s.slug === slug);
      setService(mockService);
      setError("Using cached data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2 mb-8"></div>
            <div className="h-64 bg-gray-300 rounded"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {t('serviceNotFound')}
            </h1>
            <p className="text-gray-600 mb-8">
              {t('serviceNotFoundDesc')}
            </p>
            <Link to="/">
              <Button className="bg-orange-500 hover:bg-orange-600">
                {t('backToHome')}
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const getCategoryName = (category) => {
    switch (category) {
      case "nonTariff": return t('nonTariffRegulation');
      case "trademark": return t('trademark');
      case "marking": return t('markingAndCZ');
      default: return "Прочее";
    }
  };

  const getDetailedDescription = (serviceId, language) => {
    const descriptions = {
      RU: {
        1: {
          overview: "Нотификация ФСБ РФ — обязательная процедура уведомления Федеральной службы безопасности о ввозе и использовании шифровальных (криптографических) средств на территории Российской Федерации.",
          requirements: [
            "Техническое описание оборудования на русском языке",
            "Документы, подтверждающие право собственности",
            "Сертификат или декларация соответствия (при наличии)",
            "Документы о назначении оборудования"
          ],
          process: [
            "Анализ технической документации нашими экспертами",
            "Определение алгоритмов и протоколов шифрования",
            "Подготовка пакета документов для ФСБ",
            "Подача уведомления в территориальный орган ФСБ",
            "Получение подтверждения о регистрации уведомления"
          ],
          benefits: [
            "Легальное использование криптографических средств",
            "Избежание административных штрафов",
            "Соответствие требованиям законодательства РФ",
            "Профессиональная экспертиза наших специалистов"
          ]
        },
        3: {
          overview: "Лицензия Минпромторга необходима для осуществления деятельности по разработке, производству, распространению шифровальных (криптографических) средств, информационных систем и телекоммуникационных систем.",
          requirements: [
            "Учредительные документы организации",
            "Документы о квалификации специалистов",
            "Техническое описание продукции",
            "Программа обеспечения качества"
          ],
          process: [
            "Подготовка пакета документов",
            "Экспертиза соответствия лицензионным требованиям",
            "Подача заявления в Минпромторг России",
            "Рассмотрение заявления экспертной комиссией",
            "Получение лицензии"
          ],
          benefits: [
            "Право на осуществление лицензируемой деятельности",
            "Возможность участия в государственных закупках",
            "Повышение доверия клиентов и партнеров",
            "Соответствие требованиям законодательства"
          ]
        }
      },
      EN: {
        1: {
          overview: "FSB RF notification is a mandatory procedure for notifying the Federal Security Service about the import and use of encryption (cryptographic) means on the territory of the Russian Federation.",
          requirements: [
            "Technical description of equipment in Russian language",
            "Documents confirming ownership rights",
            "Certificate or declaration of conformity (if available)",
            "Documents on equipment purpose"
          ],
          process: [
            "Analysis of technical documentation by our experts",
            "Identification of encryption algorithms and protocols",
            "Preparation of document package for FSB",
            "Submission of notification to territorial FSB authority",
            "Receiving confirmation of notification registration"
          ],
          benefits: [
            "Legal use of cryptographic means",
            "Avoiding administrative fines",
            "Compliance with RF legislation requirements",
            "Professional expertise of our specialists"
          ]
        },
        3: {
          overview: "Minpromtorg license is required for activities in development, production, distribution of encryption (cryptographic) means, information systems and telecommunication systems.",
          requirements: [
            "Organization's founding documents",
            "Documents on specialists' qualifications",
            "Technical description of products",
            "Quality assurance program"
          ],
          process: [
            "Preparation of document package",
            "Expertise of compliance with licensing requirements",
            "Application submission to Minpromtorg Russia",
            "Application review by expert commission",
            "License obtaining"
          ],
          benefits: [
            "Right to carry out licensed activities",
            "Opportunity to participate in government procurements",
            "Increased trust from clients and partners",
            "Compliance with legislation requirements"
          ]
        }
      }
    };

    const defaultDescription = {
      overview: language === 'EN' 
        ? `Detailed information about the "${service.title}" service. Our specialists will help you obtain the necessary documents in the shortest possible time.`
        : `Подробная информация об услуге "${service.title}". Наши специалисты помогут вам получить необходимые документы в кратчайшие сроки.`,
      requirements: language === 'EN' 
        ? [
            "Application in established form",
            "Package of founding documents",
            "Technical documentation",
            "Additional documents as required"
          ]
        : [
            "Заявление установленной формы",
            "Пакет учредительных документов",
            "Техническая документация",
            "Дополнительные документы по требованию"
          ],
      process: language === 'EN' 
        ? [
            "Consultation and document analysis",
            "Preparation of necessary document package",
            "Document submission to relevant authority",
            "Process support and monitoring",
            "Receiving ready documents"
          ]
        : [
            "Консультация и анализ документов",
            "Подготовка необходимого пакета документов",
            "Подача документов в соответствующий орган",
            "Сопровождение процесса рассмотрения",
            "Получение готовых документов"
          ],
      benefits: language === 'EN' 
        ? [
            "Professional support",
            "Minimal processing time",
            "Guarantee of positive result",
            "Full compliance with legislation requirements"
          ]
        : [
            "Профессиональное сопровождение",
            "Минимальные сроки оформления",
            "Гарантия положительного результата",
            "Полное соответствие требованиям законодательства"
          ]
    };

    return descriptions[language]?.[serviceId] || defaultDescription;
  };

  const details = getDetailedDescription(service.id, currentLanguage);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Breadcrumbs */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-orange-500">
              {t('home')}
            </Link>
            <span className="text-gray-400">/</span>
            <Link to="/#services" className="text-gray-500 hover:text-orange-500">
              {t('services')}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900">{service.title}</span>
          </div>
        </div>
      </div>

      {/* Service Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main content */}
          <div className="lg:col-span-2 order-2 lg:order-1">
            <div className="mb-8">
              <Link to="/" className="inline-flex items-center text-orange-500 hover:text-orange-600 mb-6">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('backToHome')}
              </Link>
              
              <div className="mb-4">
                <span className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full mb-4">
                  {getCategoryName(service.category)}
                </span>
              </div>
              
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {service.title}
              </h1>
              
              <div className="flex items-center space-x-4 text-gray-600 mb-8">
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5" />
                  <span>{t('timeframe')}: {service.timeframe}</span>
                </div>
              </div>
            </div>

            {/* Overview */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>{t('serviceDescription')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  {details.overview}
                </p>
              </CardContent>
            </Card>

            {/* Requirements */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>{t('requiredDocuments')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {details.requirements.map((req, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{req}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Process */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>{t('processingSteps')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {details.process.map((step, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </div>
                      <p className="text-gray-600 mt-1">{step}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Benefits */}
            <Card>
              <CardHeader>
                <CardTitle>{t('workingAdvantages')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {details.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="lg:sticky lg:top-8 space-y-6">
              {/* Contact form */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('orderService')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <form className="space-y-4">
                    <div>
                      <input
                        type="text"
                        placeholder={t('yourName')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        placeholder={t('email')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <input
                        type="tel"
                        placeholder={t('phone')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <textarea
                        placeholder={t('comment')}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-orange-500 focus:ring-orange-500"
                      ></textarea>
                    </div>
                    <Button className="w-full bg-orange-500 hover:bg-orange-600">
                      {t('sendApplication')}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Contact info */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('contacts')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-orange-500" />
                    <span className="text-gray-600">+7 (499) 284-29-88</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-orange-500" />
                    <span className="text-gray-600">mail@velox-hint.com</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-4">
                    {t('freeConsultationDesc')}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ServicePage;